import {
	getBaseUrlForPath,
	LINKPAY_AUTH0_AUDIENCE,
	LINKPAY_AUTH0_CLIENT_ID,
	LINKPAY_AUTH0_REDIRECT_URI,
	LINKPAY_AUTH0_DOMAIN,
	LINKPAY_AUTH0_SCOPE,
	VITE_BASE_URL,
} from "../envconst";
import { PATH } from "../../router/path";

/**
 * Where LinkPass hands the customer back: the payment page of the stall they were already on.
 * It is a page the app already serves, so signing in adds no route and no landing page — the
 * customer returns to what they were doing, with the same order still waiting to be paid for.
 */
const DEFAULT_LINKPAY_AUTH0_REDIRECT_URI = PATH.PAYMENT;
const DEFAULT_AUTH0_AUDIENCE = "https://api.fairprice.com.sg";
const DEFAULT_AUTH0_SCOPE = "openid profile email offline_access";

/**
 * The return address, rooted at the stall the customer is currently in rather than at
 * the build's own base — which is "/" or "/develop/" and never a stall path. A return address
 * computed from the build base sends the customer to a page with no stall, where the session
 * cookie is out of path scope and nothing can bring them back.
 */
const resolveStallUrl = (pathOrUrl: string): string => {
	const stallBase = getBaseUrlForPath(globalThis.location.pathname);
	const base = stallBase.endsWith("/") ? stallBase : `${stallBase}/`;

	return new URL(pathOrUrl.replace(/^\//, ""), new URL(base, globalThis.location.origin)).toString();
};

const hasLinkPayAuthEnv = Boolean(LINKPAY_AUTH0_DOMAIN && LINKPAY_AUTH0_CLIENT_ID);

if (!hasLinkPayAuthEnv) {
	const missing = [
		!LINKPAY_AUTH0_DOMAIN && "VITE_LINKPAY_AUTH0_DOMAIN",
		!LINKPAY_AUTH0_CLIENT_ID && "VITE_LINKPAY_AUTH0_CLIENT_ID",
	].filter((name): name is string => name !== false);

	console.error(`LinkPass disabled — this build is missing: ${missing.join(", ")}`);
}

/**
 * Single source of truth: does this build have LinkPass? Everything else derives from this
 * flag instead of checking the config itself.
 */
export const isLinkPayAuthAvailable = hasLinkPayAuthEnv;

/**
 * Call only after checking isLinkPayAuthAvailable — the throw is a safety net, not a flow.
 *
 * Built per call, never cached: redirectUri is derived from the stall the customer is in
 * RIGHT NOW. This module loads eagerly at app boot, at whatever URL the app opened on —
 * caching a location-derived value there would freeze a boot-time (possibly stall-less)
 * address into every later sign-in.
 */
export const getLinkPayAuthConfig = () => {
	if (!hasLinkPayAuthEnv) {
		throw new Error(
			"LinkPass is not configured for this build: missing VITE_LINKPAY_AUTH0_DOMAIN or VITE_LINKPAY_AUTH0_CLIENT_ID."
		);
	}

	return {
		domain: LINKPAY_AUTH0_DOMAIN as string,
		clientId: LINKPAY_AUTH0_CLIENT_ID as string,
		// The payment page of the stall the customer is on — a page the app already
		// serves, registered on the LinkPass tenant (theirs to vary via the env var).
		redirectUri: resolveStallUrl(LINKPAY_AUTH0_REDIRECT_URI || DEFAULT_LINKPAY_AUTH0_REDIRECT_URI),
		audience: LINKPAY_AUTH0_AUDIENCE || DEFAULT_AUTH0_AUDIENCE,
		scope: LINKPAY_AUTH0_SCOPE || DEFAULT_AUTH0_SCOPE,
	};
};

const customAuthorizationParams = {
	new_sso_login: "true",
	use_mypersona_api: "true",
};

export const getLinkPayAuthorizeParams = () => {
	const { redirectUri, scope, audience } = getLinkPayAuthConfig();

	return {
		redirect_uri: redirectUri,
		scope,
		// Auth0 accepts one API identifier per /authorize request.
		audience,
		...customAuthorizationParams,
	};
};

/** Params for silent token refresh (no redirect_uri). */
export const getLinkPayAuthTokenParams = () => {
	const { redirect_uri: _redirectUri, ...tokenParams } = getLinkPayAuthorizeParams();

	return tokenParams;
};
